import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  MapPin,
  Briefcase,
  Clock,
  ArrowRight,
  Heart,
  Rocket,
  Globe2,
  GraduationCap,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Container, SectionHeading, Eyebrow } from "@/components/common/Section";
import Reveal from "@/components/common/Reveal";
import { FieldText, FieldTextarea, isEmail } from "@/components/common/FormField";
import { fetchJobs, submitApplication } from "@/lib/apiClient";

const perks = [
  { icon: Globe2, title: "Remote-first", desc: "Work from anywhere in the US." },
  { icon: Rocket, title: "Real impact", desc: "Ship software that serves communities." },
  { icon: Heart, title: "Great benefits", desc: "Health, dental, and vision covered." },
  { icon: GraduationCap, title: "Growth budget", desc: "Learning and development stipend." },
];

const emptyApp = { name: "", email: "", location: "", linkedin: "", portfolio: "", cover_letter: "" };

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeJob, setActiveJob] = useState(null);
  const [form, setForm] = useState(emptyApp);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchJobs()
      .then(setJobs)
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  const openApply = (job) => {
    setActiveJob(job);
    setForm(emptyApp);
    setErrors({});
  };

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!isEmail(form.email)) e.email = "Enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await submitApplication({
        name: form.name.trim(),
        email: form.email.trim(),
        role: activeJob.title,
        location: form.location.trim() || null,
        linkedin: form.linkedin.trim() || null,
        portfolio: form.portfolio.trim() || null,
        cover_letter: form.cover_letter.trim() || null,
      });
      toast.success(res.message || "Application received!");
      setActiveJob(null);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <section className="relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="bg-sentira-grid bg-radial-fade pointer-events-none absolute inset-0 opacity-60" />
        <Container className="relative py-20 md:py-24">
          <Reveal>
            <Eyebrow>Careers at Sentira</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.08] tracking-[-0.02em] sm:text-5xl">
              Build software that keeps communities running
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              We're a small, senior, remote-first team shipping enterprise
              software for agencies and enterprises. Join us in building Licara.
            </p>
          </Reveal>
        </Container>
      </section>

      <section className="pb-4">
        <Container>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {perks.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="h-full rounded-[var(--radius-card)] border border-border bg-card p-5">
                  <p.icon className="h-5 w-5 text-primary" />
                  <h4 className="mt-3 text-base font-semibold">{p.title}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-24">
        <Container>
          <SectionHeading eyebrow="Open roles" title="Come build with us" />
          <div className="mt-10 space-y-4" data-testid="careers-job-list">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-28 w-full rounded-xl bg-secondary" />
              ))
            ) : jobs.length === 0 ? (
              <p className="text-muted-foreground">
                No open roles right now — but we're always meeting great people.{" "}
                <a href="/contact" className="text-primary hover:underline">Say hello</a>.
              </p>
            ) : (
              jobs.map((job, i) => (
                <Reveal key={job.id} delay={i * 0.05}>
                  <div
                    className="group rounded-[var(--radius-card)] border border-border bg-card p-6 transition-colors hover:border-[rgba(125,227,218,0.35)]"
                    data-testid={`careers-job-card-${job.id}`}
                  >
                    <div className="flex flex-col justify-between gap-5 md:flex-row md:items-center">
                      <div className="max-w-2xl">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-semibold">{job.title}</h3>
                          <Badge variant="secondary" className="border-border bg-secondary text-muted-foreground">
                            {job.department}
                          </Badge>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                          {job.summary}
                        </p>
                        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-primary" /> {job.location}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Briefcase className="h-3.5 w-3.5 text-primary" /> {job.department}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5 text-primary" /> {job.type}
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={() => openApply(job)}
                        className="shrink-0 bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]"
                        data-testid={`careers-job-card-apply-button-${job.id}`}
                      >
                        Apply
                        <ArrowRight className="ml-1.5 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Reveal>
              ))
            )}
          </div>
        </Container>
      </section>

      <Dialog open={!!activeJob} onOpenChange={(o) => !o && setActiveJob(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-card sm:max-w-lg" data-testid="careers-application-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Apply — {activeJob?.title}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {activeJob?.location} · {activeJob?.type}
            </DialogDescription>
          </DialogHeader>

          {activeJob && (
            <div className="mb-2 space-y-4 rounded-xl border border-border bg-[hsl(220_30%_8%)] p-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-primary">What you'll do</p>
                <ul className="mt-2 space-y-1.5">
                  {activeJob.responsibilities.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" data-testid="careers-application-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldText id="a-name" label="Full name" required value={form.name} onChange={set("name")} error={errors.name} testId="careers-application-name-input" placeholder="Jane Doe" />
              <FieldText id="a-email" label="Email" required type="email" value={form.email} onChange={set("email")} error={errors.email} testId="careers-application-email-input" placeholder="jane@email.com" />
            </div>
            <FieldText id="a-location" label="Location" value={form.location} onChange={set("location")} testId="careers-application-location-input" placeholder="Austin, TX" />
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldText id="a-linkedin" label="LinkedIn" value={form.linkedin} onChange={set("linkedin")} testId="careers-application-linkedin-input" placeholder="linkedin.com/in/…" />
              <FieldText id="a-portfolio" label="Portfolio / GitHub" value={form.portfolio} onChange={set("portfolio")} testId="careers-application-portfolio-input" placeholder="github.com/…" />
            </div>
            <FieldTextarea id="a-cover" label="Why Sentira?" rows={4} value={form.cover_letter} onChange={set("cover_letter")} testId="careers-application-cover-input" placeholder="Tell us a bit about yourself and why you're interested..." />
            <Button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]" data-testid="careers-application-submit-button">
              {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>) : "Submit application"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
